import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Building2, Plus, Users, Crown, ShieldCheck, User, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const roleBadge = (role) => {
  const map = {
    owner: { label: 'Owner', icon: <Crown className="w-3 h-3" />, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    admin: { label: 'Admin', icon: <ShieldCheck className="w-3 h-3" />, cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    member: { label: 'Member', icon: <User className="w-3 h-3" />, cls: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  };
  const r = map[role] || map.member;
  return (
    <Badge variant="outline" className={`flex items-center gap-1 ${r.cls}`}>
      {r.icon}
      <span className="text-[10px]">{r.label}</span>
    </Badge>
  );
};

export const OrganizationsPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/orgs');
      setOrgs(res.data.organizations || []);
    } catch {
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const handleCreate = async () => {
    if (name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post('/orgs', { name: name.trim(), description: description.trim() });
      toast.success('Organization created');
      setCreateOpen(false);
      setName('');
      setDescription('');
      navigate(`/organizations/${res.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="organizations-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-indigo-600" />
            Organizations
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            Collaborate with your team. You're a member of {orgs.length} organization{orgs.length === 1 ? '' : 's'}.
          </p>
        </div>
        <Button
          className="bg-indigo-600 hover:bg-indigo-700"
          onClick={() => setCreateOpen(true)}
          data-testid="create-org-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New organization
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : orgs.length === 0 ? (
        <Card className="border-dashed border-zinc-200">
          <CardContent className="p-12 text-center">
            <Building2 className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <h3 className="font-semibold text-zinc-900">No organizations yet</h3>
            <p className="text-sm text-zinc-500 mt-1 mb-4">
              Create your first organization to start collaborating with teammates.
            </p>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={() => setCreateOpen(true)}
              data-testid="create-org-empty-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create organization
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {orgs.map((org) => (
            <Card
              key={org.id}
              className="border-zinc-200 hover:border-indigo-300 transition-colors cursor-pointer group"
              onClick={() => navigate(`/organizations/${org.id}`)}
              data-testid={`org-card-${org.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  {roleBadge(org.role)}
                </div>
                <CardTitle className="text-base mt-3 group-hover:text-indigo-600 transition-colors">
                  {org.name}
                </CardTitle>
                {org.description && (
                  <CardDescription className="line-clamp-2">{org.description}</CardDescription>
                )}
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between text-xs text-zinc-500">
                  <span className="flex items-center gap-1.5">
                    <Users className="w-3.5 h-3.5" />
                    {org.member_count} member{org.member_count === 1 ? '' : 's'}
                  </span>
                  <ArrowRight className="w-4 h-4 text-zinc-400 group-hover:text-indigo-600 transition-colors" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-org-dialog">
          <DialogHeader>
            <DialogTitle>Create organization</DialogTitle>
            <DialogDescription>
              You'll automatically become the owner. Invite teammates after creation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="org-name">Name</Label>
              <Input
                id="org-name"
                placeholder="Acme Marketing"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                data-testid="org-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="org-desc">Description (optional)</Label>
              <Textarea
                id="org-desc"
                placeholder="What does this organization work on?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                data-testid="org-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleCreate}
              disabled={creating || name.trim().length < 2}
              data-testid="create-org-submit"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
